if (!customElements.get('invictus-configurator')) {
  customElements.define(
    'invictus-configurator',
    class InvictusConfigurator extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('form');
        this.submitButton = this.form?.querySelector('[name="add"]');
        this.notice = this.querySelector('[data-manual-notice]');

        if (!this.form || !this.submitButton || !this.notice) return;

        this.querySelectorAll('[data-review-control]').forEach((control) => {
          control.disabled = false;
        });

        if (this.dataset.addonReady === 'true') {
          this.querySelectorAll('[data-js-control]').forEach((control) => {
            control.disabled = false;
          });
          this.querySelector('[data-finish-fallback]')?.setAttribute('disabled', 'disabled');
        }

        this.retiredVariantIds = new Set((this.dataset.retiredVariantIds || '').split(/\s+/).filter(Boolean));
        this.blockedVariantIds = new Set((this.dataset.blockedVariantIds || '').split(/\s+/).filter(Boolean));
        this.addEventListener('change', () => this.update());
        this.addEventListener('input', () => this.update());
        this.form.addEventListener('change', (event) => {
          if (event.target.matches('[name="id"]')) this.update();
        });
        this.form.addEventListener(
          'submit',
          (event) => {
            if (!this.needsManualReview) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.notice.hidden = false;
            this.notice.focus?.();
          },
          true
        );
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, () => this.update());
        }
        this.update();
      }

      disconnectedCallback() {
        this.unsubscribeVariantChange?.();
      }

      getSelectedAddons() {
        const addons = [];

        this.querySelectorAll('[data-addon-control]').forEach((control) => {
          if (control.disabled) return;

          let source = control;
          if (control.tagName === 'SELECT') {
            source = control.selectedOptions[0];
          } else if (control.type === 'checkbox' && !control.checked) {
            return;
          }

          const variantId = Number(source?.dataset.addonVariantId || 0);
          if (!variantId) return;

          addons.push({
            id: variantId,
            sku: source.dataset.addonSku || '',
          });
        });

        return addons;
      }

      getCartItems(formData) {
        const addons = this.getSelectedAddons();
        if (addons.length === 0) return null;

        const parentId = Number(formData.get('id'));
        const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
        const addonMode = this.dataset.addonMode === 'linked' ? 'linked' : 'nested';
        if (!parentId) return null;

        const configurationId = globalThis.crypto?.randomUUID?.() || `invictus-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const properties = {};

        for (const [name, value] of formData.entries()) {
          const propertyMatch = name.match(/^properties\[(.+)\]$/);
          if (propertyMatch && String(value).trim() !== '') {
            properties[propertyMatch[1]] = String(value);
          }
        }

        properties._invictus_configuration_id = configurationId;
        properties._invictus_addon_mode = addonMode;

        const items = [
          {
            id: parentId,
            quantity,
            properties,
          },
        ];

        addons.forEach((addon) => {
          const addonItem = {
            id: addon.id,
            quantity,
            properties: {
              _invictus_configuration_id: configurationId,
              _invictus_addon_mode: addonMode,
              _invictus_addon_sku: addon.sku,
              _invictus_parent_product: this.dataset.productTitle || '',
            },
          };

          if (addonMode === 'nested') addonItem.parent_id = parentId;
          items.push(addonItem);
        });

        return items;
      }

      updateColourPreview() {
        const figure = this.querySelector('[data-colour-preview]');
        const image = this.querySelector('[data-colour-preview-image]');
        const select = this.querySelector('select[name="properties[Color]"]');
        if (!figure || !image || !select) return;
        const option = select.disabled ? null : select.selectedOptions[0];
        const src = option?.dataset.previewSrc || '';
        if (!src) {
          figure.hidden = true;
          return;
        }
        if (image.getAttribute('src') !== src) image.setAttribute('src', src);
        image.alt = `${this.dataset.productTitle || ''} — ${option.value}`.trim();
        figure.hidden = false;
      }

      update() {
        this.updateColourPreview();
        this.querySelectorAll('[data-toggle-target]').forEach((control) => {
          const target = document.getElementById(control.dataset.toggleTarget);
          if (!target) return;
          const targetIsEnabled = control.dataset.toggleValue
            ? control.value === control.dataset.toggleValue
            : control.checked;
          target.disabled = !targetIsEnabled;
          if (!targetIsEnabled) target.value = '';
        });

        const checkedManualControl = Array.from(this.querySelectorAll('[data-manual-review]')).some(
          (control) => control.checked
        );
        const manualValueSelected = Array.from(this.querySelectorAll('[data-manual-value]')).some(
          (control) => control.value === control.dataset.manualValue
        );
        const manualTextEntered = Array.from(this.querySelectorAll('[data-manual-on-value]')).some(
          (control) => control.value.trim() !== ''
        );
        const requiredReviewMissing = Array.from(this.querySelectorAll('[data-required-review]')).some(
          (control) => !control.disabled && control.value.trim() === ''
        );
        const currentVariantId = this.form.querySelector('[name="id"]')?.value || '';
        const invalidVariant = this.retiredVariantIds.has(currentVariantId);
        const catalogBlocked = this.blockedVariantIds.has(currentVariantId);
        const needsManualReview =
          checkedManualControl || manualValueSelected || manualTextEntered || requiredReviewMissing || invalidVariant;

        this.needsManualReview = needsManualReview;
        this.notice.hidden = !needsManualReview;
        if (needsManualReview || catalogBlocked) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute('aria-disabled');
        }

        const buttonLabel = this.submitButton.querySelector('span');
        if (buttonLabel && invalidVariant) {
          buttonLabel.textContent = this.dataset.invalidLabel;
        } else if (buttonLabel && !catalogBlocked) {
          buttonLabel.textContent = this.dataset.configureLabel;
        }
      }
    }
  );
}
